import { useEffect, useRef } from 'react';
import { useUser } from '../components/providers/UserProvider';
import gun from '../config/gun.config';

/**
 * Once per session, if the user has coordinates and no city stored,
 * reverse-geocode their location using the Nominatim API and save
 * city + country to their profile.
 */
export function useReverseGeocode(lat: number | null, lng: number | null) {
  const { user, updateUser } = useUser();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    if (!lat || !lng) return;
    if (user?.city) { done.current = true; return; } // already have city
    if (!user?.nullifierHash) return;

    done.current = true; // only attempt once

    const controller = new AbortController();
    fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=10`,
      {
        signal: controller.signal,
        headers: { 'Accept-Language': 'en' },
      }
    )
      .then(r => r.json())
      .then((data: any) => {
        const addr = data?.address || {};
        const city =
          addr.city ||
          addr.town ||
          addr.village ||
          addr.county ||
          addr.state_district ||
          addr.state ||
          '';
        const country = addr.country_code?.toUpperCase() || '';
        if (!city && !country) return;

        updateUser({ city, country });

        // Persist to GUN so other users can see location tag
        gun
          .get('stabeon_users')
          .get(user.nullifierHash)
          .get('city')
          .put(city);
        gun
          .get('stabeon_users')
          .get(user.nullifierHash)
          .get('country')
          .put(country);
      })
      .catch(() => {}); // silently ignore failures

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, user?.nullifierHash]);
}
